import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail, asList } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Plus, Upload, Trash2, ArrowLeft } from "lucide-react";
import ImageLightbox from "@/components/ImageLightbox";
import VoiceNoteCapture from "@/components/VoiceNoteCapture";
import PhotoAnnotator from "@/components/PhotoAnnotator";

const emptyItem = () => ({
  product_category_id: "",
  quantity: 1,
  measurement_value: "",
  color: "",
  weight_from: "",
  weight_to: "",
  melting: "",
  huid: false,
  order_type: "customer",
  delivery_date: "",
  images: [],
  item_remarks: "",
  voice_note_url: "",
});

// Business rules — dropdown values shared across new & edit flows.
const METAL_COLORS = ["Yellow Gold", "White Gold", "Rose Gold", "Rose-White Gold"];
const MELTING_OPTIONS = ["9kt", "14kt", "18kt", "20kt", "21kt", "22kt"];
const MIN_LEAD_DAYS = 4;

/** Returns yyyy-mm-dd for today + N days (default MIN_LEAD_DAYS). */
function minDeliveryDate(days = MIN_LEAD_DAYS) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function OrderFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = Boolean(id);

  const [categories, setCategories] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [staffs, setStaffs] = useState([]);
  // Customer picker (typeahead) — Order Team & Sales cannot browse the full
  // book, so we only ever fetch a scoped preview via /contacts/pick?q=...
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientSearching, setClientSearching] = useState(false);

  const [form, setForm] = useState({
    client_id: "",
    staff_ids: [],
    priority: "normal",
    remarks: "",
    items: [emptyItem()],
  });
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images, startIndex }
  const [annotator, setAnnotator] = useState(null); // { itemIdx, imageIdx, url }

  useEffect(() => { (async () => {
    const [{data:cats},{data:meas},{data:usrs}] = await Promise.all([
      api.get("/product-categories"),
      api.get("/measurements"),
      api.get("/users"),
    ]);
    const users = asList(usrs);
    setCategories(asList(cats));
    setMeasurements(asList(meas));
    // Support both current roles ("order_team") and legacy alias ("staff").
    setStaffs(users.filter(u => u.role === "order_team" || u.role === "staff"));

    if (isEdit) {
      const { data: o } = await api.get(`/orders/${id}`);
      setForm({
        client_id: o.client_id || "",
        staff_ids: o.staff_ids || [],
        priority: o.priority || "normal",
        remarks: o.remarks || "",
        items: (o.items || []).map(it => ({
          product_category_id: it.product_category_id || "",
          quantity: it.quantity || 1,
          measurement_value: it.measurement_value || "",
          color: it.color || "",
          weight_from: it.weight_from ?? "",
          weight_to: it.weight_to ?? "",
          melting: it.melting || "",
          huid: !!it.huid,
          order_type: it.order_type || "customer",
          delivery_date: it.delivery_date ? it.delivery_date.slice(0,10) : "",
          images: it.images || [],
          item_remarks: it.item_remarks || "",
          voice_note_url: it.voice_note_url || "",
          voice_note_transcript: it.voice_note_transcript || "",
        })),
      });
      if (o.client_id) {
        try {
          const { data } = await api.get(`/contacts/${o.client_id}`);
          setSelectedContact(data);
          setClientQuery(`${data.name}${data.mobile ? ` · ${data.mobile}` : ""}`);
        } catch (err) { console.warn("Failed to prefetch client contact:", err); }
      }
    }
  })(); }, [id, isEdit]);

  // reload contact for preferences when client changes
  useEffect(() => {
    if (!form.client_id) { setSelectedContact(null); return; }
    (async () => { try { const { data } = await api.get(`/contacts/${form.client_id}`); setSelectedContact(data); } catch (err) { console.warn("Failed to load client preferences:", err); } })();
  }, [form.client_id]);

  // Debounced customer typeahead. Fires only for >=2 chars; server caps to 20 results.
  useEffect(() => {
    const term = clientQuery.trim();
    if (term.length < 2) { setClientResults([]); return; }
    let ignore = false;
    setClientSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/pick", { params: { q: term } });
        if (!ignore) setClientResults(asList(data));
      } catch (err) {
        if (!ignore) setClientResults([]);
      } finally {
        if (!ignore) setClientSearching(false);
      }
    }, 220);
    return () => { ignore = true; clearTimeout(t); };
  }, [clientQuery]);

  const catById = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);

  const measForCategory = (catId) => {
    const cat = catById[catId];
    if (!cat) return [];
    return measurements.filter(m => m.type === cat.measurement_type);
  };

  const updateItem = (idx, patch) => {
    setForm(f => ({ ...f, items: f.items.map((it,i) => i===idx ? { ...it, ...patch } : it) }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_,i)=>i!==idx) }));

  const uploadImage = async (idx, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateItem(idx, { images: [...(form.items[idx].images || []), { url: data.url, thumb_url: data.thumb_url }] });
    } catch (e) { toast.error("Upload failed"); }
  };

  const submit = async () => {
    // Validate
    if (form.items.length === 0) return toast.error("Add at least one item");
    const minDate = minDeliveryDate();
    for (const it of form.items) {
      if (!it.product_category_id) return toast.error("Each item needs a category");
      if (it.delivery_date && it.delivery_date < minDate) {
        return toast.error(`Delivery date must be at least ${MIN_LEAD_DAYS} days from today`);
      }
    }
    const payload = {
      ...form,
      client_id: form.client_id || null,
      karigar_id: null,
      items: form.items.map(it => ({
        ...it,
        quantity: Number(it.quantity) || 1,
        weight_from: it.weight_from === "" ? null : Number(it.weight_from),
        weight_to: it.weight_to === "" ? null : Number(it.weight_to),
        delivery_date: it.delivery_date ? new Date(it.delivery_date).toISOString() : null,
        item_remarks: it.item_remarks?.trim() || null,
        voice_note_url: it.voice_note_url || null,
        voice_note_transcript: it.voice_note_transcript || null,
      })),
    };
    try {
      if (isEdit) {
        await api.patch(`/orders/${id}`, payload);
        toast.success("Order updated");
      } else {
        const { data } = await api.post("/orders", payload);
        toast.success(`Order ${data.reference_number} created`);
        nav(`/orders/${data.id}`); return;
      }
      nav(`/orders/${id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="order-form-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Manufacturing</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">{isEdit ? "Edit Order" : "New Order"}</h1>
      </div>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Client</Label>
                <button
                  type="button"
                  onClick={() => setNewContactOpen(true)}
                  className="text-xs text-[#0F5E39] hover:underline inline-flex items-center gap-1"
                  data-testid="order-new-contact-btn"
                >+ Add new customer</button>
              </div>
              <ClientPicker
                selected={selectedContact}
                query={clientQuery}
                results={clientResults}
                searching={clientSearching}
                open={clientPickerOpen}
                onQueryChange={(v) => { setClientQuery(v); setClientPickerOpen(true); }}
                onFocus={() => setClientPickerOpen(true)}
                onBlur={() => setTimeout(() => setClientPickerOpen(false), 150)}
                onPick={(c) => {
                  setForm(f => ({ ...f, client_id: c.id }));
                  setSelectedContact(c);
                  setClientQuery(`${c.name}${c.mobile ? ` · ${c.mobile}` : ""}`);
                  setClientPickerOpen(false);
                }}
                onClear={() => {
                  setForm(f => ({ ...f, client_id: "" }));
                  setSelectedContact(null);
                  setClientQuery("");
                  setClientResults([]);
                }}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Priority</Label>
              <Select value={form.priority} onValueChange={(v)=>setForm({...form, priority:v})}>
                <SelectTrigger className="mt-1.5" data-testid="order-priority-select"><SelectValue/></SelectTrigger>
                <SelectContent>{["urgent","fast","normal"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Staff (multi)</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {staffs.map(s => {
                const active = form.staff_ids.includes(s.id);
                return (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => setForm(f => ({ ...f, staff_ids: active ? f.staff_ids.filter(x=>x!==s.id) : [...f.staff_ids, s.id] }))}
                    className={`text-sm px-3 py-1.5 rounded-full border ${active ? "bg-[#C9A24B] text-white border-[#C9A24B]" : "bg-white text-[#4A5A50] border-[#E4DFCF] hover:bg-[#F1EDDF]"}`}
                    data-testid={`staff-chip-${s.id}`}
                  >{s.name}</button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Remarks</Label>
            <Textarea rows={2} value={form.remarks} onChange={(e)=>setForm({...form, remarks:e.target.value})} className="mt-1.5" data-testid="order-remarks"/>
          </div>
        </CardContent>
      </Card>

      {/* Product Preferences for selected contact */}
      {selectedContact?.preferences && (
        <Card className="border-[#C9A24B]/40 bg-[#F6EED8]/40 rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#6E5416] mb-2">
              <span>Client preferences</span> · <span>{selectedContact.name}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-[10px] uppercase text-[#4A5A50]">Metals</div>{(selectedContact.preferences.metals||[]).join(", ") || "—"}</div>
              <div><div className="text-[10px] uppercase text-[#4A5A50]">Purity</div>{(selectedContact.preferences.purity||[]).join(", ") || "—"}</div>
              <div><div className="text-[10px] uppercase text-[#4A5A50]">Styles</div>{(selectedContact.preferences.styles||[]).join(", ") || "—"}</div>
              <div><div className="text-[10px] uppercase text-[#4A5A50]">Budget</div>₹{selectedContact.preferences.budget_min || 0} - ₹{selectedContact.preferences.budget_max || 0}</div>
            </div>
            {selectedContact.preferences.notes && <div className="mt-2 text-xs text-[#4A5A50]">{selectedContact.preferences.notes}</div>}
          </CardContent>
        </Card>
      )}

      {form.items.map((it, idx) => (
        <Card key={idx} className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-serif text-lg">Item {idx+1}</div>
              {form.items.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-600" data-testid={`remove-item-${idx}`}><Trash2 size={14}/></Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={it.product_category_id || undefined} onValueChange={(v)=>updateItem(idx, { product_category_id: v, measurement_value: "" })}>
                  <SelectTrigger className="mt-1" data-testid={`item-category-${idx}`}><SelectValue placeholder="Select category"/></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Measurement</Label>
                <Select value={it.measurement_value || undefined} onValueChange={(v)=>updateItem(idx, { measurement_value: v })} disabled={!it.product_category_id}>
                  <SelectTrigger className="mt-1" data-testid={`item-measurement-${idx}`}><SelectValue placeholder="Pick"/></SelectTrigger>
                  <SelectContent>{measForCategory(it.product_category_id).map(m => <SelectItem key={m.id} value={m.value}>{m.value}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Quantity</Label>
                <Input type="number" min="1" value={it.quantity} onChange={(e)=>updateItem(idx, { quantity: e.target.value })} className="mt-1"/>
              </div>
              <div>
                <Label className="text-xs">Color</Label>
                <Select value={it.color || undefined} onValueChange={(v)=>updateItem(idx, { color: v })}>
                  <SelectTrigger className="mt-1" data-testid={`item-color-${idx}`}><SelectValue placeholder="Pick colour"/></SelectTrigger>
                  <SelectContent>{METAL_COLORS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Weight from (g)</Label>
                <Input type="number" step="0.01" value={it.weight_from} onChange={(e)=>updateItem(idx, { weight_from: e.target.value })} className="mt-1"/>
              </div>
              <div>
                <Label className="text-xs">Weight to (g)</Label>
                <Input type="number" step="0.01" value={it.weight_to} onChange={(e)=>updateItem(idx, { weight_to: e.target.value })} className="mt-1"/>
              </div>
              <div>
                <Label className="text-xs">Melting</Label>
                <Select value={it.melting || undefined} onValueChange={(v)=>updateItem(idx, { melting: v })}>
                  <SelectTrigger className="mt-1" data-testid={`item-melting-${idx}`}><SelectValue placeholder="Pick purity"/></SelectTrigger>
                  <SelectContent>{MELTING_OPTIONS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Order type</Label>
                <Select value={it.order_type} onValueChange={(v)=>updateItem(idx, { order_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="stock">stock</SelectItem><SelectItem value="customer">customer</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Delivery date <span className="text-[#4A5A50]">(min +{MIN_LEAD_DAYS} days)</span></Label>
                <Input
                  type="date"
                  value={it.delivery_date}
                  min={minDeliveryDate()}
                  onChange={(e)=>updateItem(idx, { delivery_date: e.target.value })}
                  className="mt-1"
                  data-testid={`item-delivery-${idx}`}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={it.huid} onCheckedChange={(v)=>updateItem(idx, { huid: v })} data-testid={`item-huid-${idx}`}/>
                <Label className="text-sm">HUID required</Label>
              </div>
            </div>

            <div>
              <Label className="text-xs">Design references</Label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {(it.images || []).map((img, k) => (
                  <div key={k} className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#E4DFCF] group">
                    <button type="button" onClick={() => setLightbox({ images: it.images, startIndex: k })} className="w-full h-full" data-testid={`form-item-image-${idx}-${k}`}>
                      <img src={fileUrl(img.thumb_url || img.url)} loading="lazy" className="w-full h-full object-cover group-hover:opacity-90 transition" alt=""/>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAnnotator({ itemIdx: idx, imageIdx: k, url: img.url || img.thumb_url })}
                      className="absolute bottom-1 left-1 bg-black/60 text-white rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition"
                      data-testid={`form-item-annotate-${idx}-${k}`}
                    >Annotate</button>
                    <button
                      type="button"
                      onClick={() => updateItem(idx, { images: it.images.filter((_,x)=>x!==k) })}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 z-10"
                    ><X size={12}/></button>
                  </div>
                ))}
                <label className="w-20 h-20 border-2 border-dashed border-[#E4DFCF] rounded-lg flex items-center justify-center cursor-pointer hover:border-[#C9A24B] text-[#4A5A50]" data-testid={`upload-image-${idx}`}>
                  <Upload size={16}/>
                  <input type="file" accept="image/*" className="hidden" onChange={(e)=>e.target.files?.[0] && uploadImage(idx, e.target.files[0])}/>
                </label>
              </div>
            </div>

            <div>
              <Label className="text-xs">Item remarks</Label>
              <Textarea
                rows={2}
                value={it.item_remarks || ""}
                onChange={(e) => updateItem(idx, { item_remarks: e.target.value })}
                placeholder="Any special instruction for this item…"
                className="mt-1.5"
                data-testid={`item-remarks-${idx}`}
              />
            </div>

            <div>
              <Label className="text-xs">Voice note</Label>
              <VoiceNoteCapture
                url={it.voice_note_url}
                transcript={it.voice_note_transcript}
                onChange={(url, transcript) => updateItem(idx, { voice_note_url: url, voice_note_transcript: transcript || "" })}
                testIdPrefix={`item-voice-${idx}`}
              />
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={addItem} className="rounded-full" data-testid="add-item-btn"><Plus size={14} className="mr-1"/> Add another item</Button>
        <Button onClick={submit} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="save-order-btn">{isEdit ? "Save changes" : "Create Order"}</Button>
      </div>

      <NewContactDialog
        open={newContactOpen}
        onClose={() => setNewContactOpen(false)}
        onCreated={(c) => {
          // Preselect the freshly created customer — order flow continues without a page hop.
          setForm(f => ({ ...f, client_id: c.id }));
          setSelectedContact(c);
          setClientQuery(`${c.name}${c.mobile ? ` · ${c.mobile}` : ""}`);
          setClientResults([]);
          setClientPickerOpen(false);
          setNewContactOpen(false);
          toast.success("Customer added");
        }}
      />
      <ImageLightbox
        open={!!lightbox}
        images={lightbox?.images || []}
        startIndex={lightbox?.startIndex || 0}
        onClose={() => setLightbox(null)}
        resolveSrc={(img) => fileUrl(img.url || img.thumb_url)}
      />
      <PhotoAnnotator
        open={!!annotator}
        imageUrl={annotator?.url}
        onClose={() => setAnnotator(null)}
        onSaved={(url) => {
          const { itemIdx, imageIdx } = annotator || {};
          const newImgs = [...(form.items[itemIdx]?.images || [])];
          if (imageIdx != null && newImgs[imageIdx]) {
            newImgs[imageIdx] = { url, thumb_url: url };
          }
          updateItem(itemIdx, { images: newImgs });
          setAnnotator(null);
        }}
      />
    </div>
  );
}

/** Inline "Add new customer" dialog invoked from the New Order client picker. */
function NewContactDialog({ open, onClose, onCreated }) {
  const [f, setF] = React.useState({ name: "", mobile: "", email: "", address: "", contact_type: "customer" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [duplicates, setDuplicates] = React.useState([]);
  React.useEffect(() => {
    if (open) {
      setF({ name: "", mobile: "", email: "", address: "", contact_type: "customer" });
      setError("");
      setDuplicates([]);
    }
  }, [open]);

  // Debounced duplicate check — fires when name/mobile look "complete-ish".
  React.useEffect(() => {
    const n = (f.name || "").trim();
    const m = (f.mobile || "").trim();
    if (n.length < 3 && m.length < 6) { setDuplicates([]); return; }
    let ignore = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/duplicate-check", { params: { name: n, mobile: m } });
        if (!ignore) setDuplicates(data.matches || []);
      } catch (e) { if (!ignore) setDuplicates([]); }
    }, 300);
    return () => { ignore = true; clearTimeout(t); };
  }, [f.name, f.mobile]);

  const submit = async () => {
    if (!f.name.trim()) { setError("Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      const { data } = await api.post("/contacts", payload);
      onCreated(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="order-new-contact-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Add new customer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-contact-name"/></div>
          <div><Label>Mobile</Label><Input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="new-contact-mobile"/></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}/></div>
          <div className="col-span-2"><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })}/></div>
          <div><Label>Type</Label>
            <Select value={f.contact_type} onValueChange={(v) => setF({ ...f, contact_type: v })}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">customer</SelectItem>
                <SelectItem value="lead">lead</SelectItem>
                <SelectItem value="vendor">vendor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {duplicates.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="new-contact-duplicates">
            <div className="text-[10px] uppercase tracking-widest text-amber-800 mb-1.5">
              Similar contact{duplicates.length > 1 ? "s" : ""} already exist
            </div>
            <div className="space-y-1.5">
              {duplicates.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[#0F1E15]">{d.display_name || d.name}</div>
                    <div className="text-[11px] text-[#4A5A50]">{d.mobile || "—"}{d.city ? ` · ${d.city}` : ""}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onCreated(d)}
                    className="text-xs text-[#0F5E39] hover:underline"
                    data-testid={`new-contact-attach-${d.id}`}
                  >Attach existing</button>
                </div>
              ))}
            </div>
          </div>
        )}
        {error && <div className="text-sm text-red-600" data-testid="new-contact-error">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="new-contact-submit-btn">{saving ? "Saving…" : "Save & select"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Scoped customer typeahead used from the New Order form.
 * Never lists the full book — Order Team relies on this to attach a customer
 * to an order without seeing anyone else's clients.
 */
function ClientPicker({ selected, query, results, searching, open, onQueryChange, onFocus, onBlur, onPick, onClear }) {
  return (
    <div className="mt-1.5 relative" data-testid="order-client-picker">
      <Input
        value={query}
        placeholder="Search customer by name or mobile…"
        onChange={(e) => onQueryChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid="order-client-search-input"
      />
      {selected ? (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest text-[#4A5A50] hover:text-[#0F1E15]"
          data-testid="order-client-clear-btn"
        >Clear</button>
      ) : null}
      {open && query.trim().length >= 2 ? (
        <div
          className="absolute z-20 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg border border-[#E4DFCF] bg-white shadow-lg"
          data-testid="order-client-results"
          onMouseDown={(e) => e.preventDefault()}
        >
          {searching ? (
            <div className="p-3 text-xs text-[#4A5A50]">Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-xs text-[#4A5A50]">
              No matches. Use <span className="text-[#0F5E39]">+ Add new customer</span> to create one.
            </div>
          ) : (
            results.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => onPick(c)}
                className="w-full text-left px-3 py-2 hover:bg-[#F5F0E1] border-b border-[#F5F0E1] last:border-0"
                data-testid={`order-client-result-${c.id}`}
              >
                <div className="text-sm text-[#0F1E15]">{c.display_name || c.name}</div>
                <div className="text-[11px] text-[#4A5A50]">
                  {c.mobile || "—"}{c.city ? ` · ${c.city}` : ""}
                </div>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
